import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import {
    colorForAutomationStatus,
    displayTextForAutomationStatus,
    fullDisplayTextForMonitoringMetric,
    getRolloutChecklistItemsOwners,
} from '../../helpers/displayHelpers';
import { createAppSettingsAutomationsRouteWithAutomation } from '../../helpers/routeHelpers';
import { RootState } from '../../reducers/reducers';
import {
    selectAppTimeFormatSetting,
    selectAutomationById,
    selectMonitoringMetricsById,
    selectOrgGroups,
    selectOrgUsers,
} from '../../selectors/selectors';
import {
    Automation,
    AutomationHaltUnstableRelease,
    AutomationPromoteStablePhasedRelease,
    AutomationStatus,
    AutomationType,
    ChecklistItem,
    MonitoringAutomation,
    MonitoringAutomationConditionType,
    MonitoringMetricHealthStatus,
    SubscriptionFeatureType,
    ChecklistItemStatusType,
    AutomationRun,
} from '../../types';
import { ReleaseComponentProps, ReleaseRouteComponentMatchParams } from '../../typescriptHelpers';
import { antGrey, colorWithAlpha, runwayColors, rwColors, tagStyle } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import {
    ThunderboltFilled,
    ClockCircleFilled,
    CheckCircleFilled,
    CloseCircleFilled,
    SettingOutlined,
    CaretRightOutlined,
} from '@ant-design/icons';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat, getLocaleDateTimeFormat } from '../../timeHelpers';
import Tooltip from 'antd/lib/tooltip';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { iconForAutomationStatus } from '../automations/AutomationTab';
import Collapse from 'antd/lib/collapse';

interface MonitoringAutomationProps extends ReleaseComponentProps {
    monitoringAutomations: MonitoringAutomation[];
    shouldShowDisabledUI?: boolean;
    checklistItems?: ChecklistItem[];
    automationRuns: AutomationRun[];
}

const MonitoringAutomations: React.FC<MonitoringAutomationProps> = (props: MonitoringAutomationProps) => {
    const { orgIdParam, appIdParam } = useParams<ReleaseRouteComponentMatchParams>();

    const monitoringAutomationsFromSettings = useSelector((state: RootState) => {
        return (
            props.monitoringAutomations?.reduce(
                (acc, monitoringAutomation) => {
                    const automation = selectAutomationById(state, props.appId, monitoringAutomation.id);

                    if (automation) {
                        acc[monitoringAutomation.id] = automation;
                    }

                    return acc;
                },
                {} as { [key: string]: Automation },
            ) ?? []
        );
    });

    const automationRunsById = React.useMemo(() => {
        const automationRuns = props.automationRuns ?? [];
        return automationRuns.reduce((acc, run) => {
            acc[run.id] = run;
            return acc;
        }, {});
    }, [props.automationRuns]);

    const monitoringMetricsById = useSelector((state: RootState) => selectMonitoringMetricsById(state, props.appId));
    // TODO: remove reference to orgUsers whenever possible
    const orgUsers = useSelector((state: RootState) => selectOrgUsers(state, props.orgId));
    const orgGroups = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));

    const statusTagForAutomationStatus = (
        enabled: boolean,
        automationType: AutomationType,
        status: AutomationStatus,
        statusMessage?: string,
        triggeredAt?: string,
        discardedAt?: string,
    ) => {
        let text = displayTextForAutomationStatus(status);
        let color = colorForAutomationStatus(status, false);
        let tooltipText = null;

        if (statusMessage) {
            tooltipText = statusMessage;
        }

        if (!enabled) {
            text = 'Disabled';
            color = antGrey.grey5;
        } else {
            switch (status) {
                case AutomationStatus.Active:
                    text = 'Active';
                    color = runwayColors.primary;
                    break;
                case AutomationStatus.Success: {
                    switch (automationType) {
                        case AutomationType.AutoHaltUnstablePhasedReleases: {
                            color = runwayColors.statusRed;
                            text = 'triggered';
                            tooltipText = `Triggered on: ${dayjs(triggeredAt).format(
                                getLocaleDateTimeFormat('fullYearAtTime', use24HourTimeFormat),
                            )}`;
                            break;
                        }
                        case AutomationType.AutoPromoteStablePhasedReleases: {
                            color = runwayColors.statusGreen;
                            text = 'triggered';
                            tooltipText = `Triggered on: ${dayjs(triggeredAt).format(
                                getLocaleDateTimeFormat('fullYearAtTime', use24HourTimeFormat),
                            )}`;
                            break;
                        }
                        case AutomationType.AutoPrepareRollbackRelease: {
                            color = runwayColors.primary;
                            text = 'Prepared';
                            tooltipText = 'Rollback has been prepared.';
                            break;
                        }
                        default:
                            break;
                    }
                    break;
                }
                default:
                    break;
            }

            if (automationType === AutomationType.AutoPrepareRollbackRelease && !!discardedAt) {
                color = runwayColors.statusGrey;
                text = 'Discarded';
            }
        }

        const tag = (
            <Tag style={{ ...tagStyle, color: color, borderColor: color }} color={'white'}>
                {text}
            </Tag>
        );

        if (tooltipText) {
            return <Tooltip title={tooltipText}>{tag}</Tooltip>;
        }

        return tag;
    };

    const conditionTextStyle: React.CSSProperties = { color: antGrey.grey7, fontSize: 13 };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                width: '100%',
                padding: 20,
                borderRadius: 8,
                border: `1px solid ${antGrey.grey5}`,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts} requiresTooltip>
                    <Text style={{ color: antGrey.grey8, fontWeight: 600 }}>Automations</Text>
                </PlanRestrictedFeatureWrapper>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
                {props.monitoringAutomations?.map((automation, index) => {
                    const automationFromSettings = monitoringAutomationsFromSettings[automation.id] as Automation;
                    const automationRun = automationRunsById[automation.id];

                    if (!automationFromSettings || !automationRun) {
                        return null;
                    }

                    const description = automationFromSettings.name;

                    const automationLink = createAppSettingsAutomationsRouteWithAutomation(
                        orgIdParam,
                        appIdParam,
                        automation.id,
                    );

                    const status = automationRun.status;
                    const statusMessage = automationRun.statusMessage;

                    const shouldShowMetricsData =
                        automationFromSettings.enabled &&
                        status !== AutomationStatus.Skipped &&
                        automation.id !== AutomationType.AutoPrepareRollbackRelease;

                    const automationEnabledAndReleaseNotSkipped =
                        automationFromSettings.enabled && status !== AutomationStatus.Skipped;

                    const shouldShowAutomationSteps =
                        automationFromSettings.enabled &&
                        automation.id === AutomationType.AutoPrepareRollbackRelease &&
                        !automation.discardedAt;

                    const shouldShowRollbackDiscardedText =
                        automation.id === AutomationType.AutoPrepareRollbackRelease && !!automation.discardedAt;

                    const validAssociatedMetrics = automation.associatedMetrics.filter(
                        (metric) => monitoringMetricsById[metric.id]?.thresholds?.length > 0,
                    );

                    return (
                        <PlanRestrictedFeatureWrapper
                            key={index}
                            feature={SubscriptionFeatureType.Rollouts}
                            shouldFakeDisabledState
                        >
                            <Collapse
                                bordered={false}
                                expandIcon={({ isActive }) => (
                                    <CaretRightOutlined
                                        style={{
                                            color: antGrey.grey6,
                                            fontSize: 18,
                                        }}
                                        rotate={isActive ? 90 : 0}
                                    />
                                )}
                                style={{
                                    backgroundColor: antGrey.grey2,
                                    borderRadius: 8,
                                }}
                            >
                                <Collapse.Panel
                                    key="1"
                                    header={
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                width: '100%',
                                            }}
                                        >
                                            <Space>
                                                <ThunderboltFilled
                                                    style={{
                                                        color:
                                                            automationEnabledAndReleaseNotSkipped ?
                                                                runwayColors.primary
                                                            :   antGrey.grey6,
                                                    }}
                                                />
                                                <Text
                                                    style={{
                                                        color:
                                                            automationEnabledAndReleaseNotSkipped ?
                                                                runwayColors.darkTextGrey
                                                            :   antGrey.grey6,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {description}
                                                </Text>
                                                <Link to={automationLink} onClick={(e) => e.stopPropagation()}>
                                                    <SettingOutlined style={{ color: runwayColors.primary }} />
                                                </Link>
                                            </Space>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                {statusTagForAutomationStatus(
                                                    automationFromSettings.enabled,
                                                    automationFromSettings.type,
                                                    status,
                                                    statusMessage,
                                                    automation.triggeredAt,
                                                    automation.discardedAt,
                                                )}
                                            </div>
                                        </div>
                                    }
                                    style={{
                                        backgroundColor: antGrey.grey2,
                                        border: 'none',
                                    }}
                                    className="automations-panel"
                                >
                                    {!automationFromSettings.enabled && (
                                        <div
                                            style={{
                                                backgroundColor: colorWithAlpha(rwColors.primary, 0.2),
                                                borderRadius: 5,
                                                padding: 10,
                                                marginBottom: 16,
                                            }}
                                        >
                                            <Text style={conditionTextStyle}>
                                                Go to <Link to={automationLink}>App settings &gt; Automations</Link> to
                                                enable this automation.
                                            </Text>
                                        </div>
                                    )}
                                    {props.shouldShowDisabledUI ?
                                        <div
                                            style={{
                                                backgroundColor: 'white',
                                                padding: 8,
                                                borderRadius: 4,
                                                width: '100%',
                                            }}
                                        >
                                            <Text
                                                style={{
                                                    color: antGrey.grey7,
                                                    fontSize: 12,
                                                }}
                                            >
                                                Rollout automations not available on Free plan.
                                            </Text>
                                        </div>
                                    :   <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(3, 1fr)',
                                                gap: 16,
                                                width: '100%',
                                            }}
                                        >
                                            {shouldShowMetricsData && (
                                                <ConditionsOrMetricsContainer title="Health metrics">
                                                    {validAssociatedMetrics?.length === 0 && (
                                                        <Text style={conditionTextStyle}>
                                                            No health metrics set. Go to this automation&apos;s settings
                                                            to configure metrics.
                                                        </Text>
                                                    )}
                                                    {validAssociatedMetrics?.map((metric, metricIndex) => {
                                                        const monitoringMetric = monitoringMetricsById[metric.id];

                                                        return (
                                                            <>
                                                                {!monitoringMetric?.hideOnRolloutView && (
                                                                    <Space key={metricIndex}>
                                                                        {iconForMonitoringMetricStatus(metric.status)}
                                                                        <Text
                                                                            style={{
                                                                                ...conditionTextStyle,
                                                                                color:
                                                                                    (
                                                                                        metric.status ===
                                                                                        MonitoringMetricHealthStatus.Unhealthy
                                                                                    ) ?
                                                                                        runwayColors.statusRed
                                                                                    :   conditionTextStyle.color,
                                                                            }}
                                                                        >
                                                                            {monitoringMetric &&
                                                                                fullDisplayTextForMonitoringMetric(
                                                                                    monitoringMetric,
                                                                                )}
                                                                        </Text>
                                                                    </Space>
                                                                )}
                                                            </>
                                                        );
                                                    })}
                                                </ConditionsOrMetricsContainer>
                                            )}
                                            {shouldShowMetricsData && (
                                                <ConditionsOrMetricsContainer title="Conditions">
                                                    {automation.associatedConditions.length === 0 &&
                                                        automation.checklistItems?.length === 0 && (
                                                            <Text style={conditionTextStyle}>
                                                                No conditions set. Go to this automation&apos;s settings
                                                                to configure conditions.
                                                            </Text>
                                                        )}
                                                    {automation.associatedConditions.map(
                                                        (condition, conditionIndex) => {
                                                            let text = '';
                                                            if (
                                                                condition.type ===
                                                                MonitoringAutomationConditionType.Adoption
                                                            ) {
                                                                const threshold = (
                                                                    automationFromSettings as
                                                                        | AutomationPromoteStablePhasedRelease
                                                                        | AutomationHaltUnstableRelease
                                                                )?.data?.adoptionThreshold;

                                                                if (!threshold) {
                                                                    text = '';
                                                                } else {
                                                                    text = `${threshold}% session adoption`;
                                                                }
                                                            } else if (
                                                                condition.type ===
                                                                MonitoringAutomationConditionType.PhasedReleaseDay
                                                            ) {
                                                                const threshold = (
                                                                    automationFromSettings as AutomationPromoteStablePhasedRelease
                                                                )?.data?.phasedReleaseDayThreshold;

                                                                if (!threshold) {
                                                                    text = '';
                                                                } else {
                                                                    text = `Day ${threshold} of phased release`;
                                                                }
                                                            } else if (
                                                                condition.type ===
                                                                MonitoringAutomationConditionType.StagedRolloutPercentage
                                                            ) {
                                                                const threshold = (
                                                                    automationFromSettings as
                                                                        | AutomationPromoteStablePhasedRelease
                                                                        | AutomationHaltUnstableRelease
                                                                )?.data?.stagedRolloutPercentageThreshold;

                                                                if (!threshold) {
                                                                    text = '';
                                                                } else {
                                                                    text = `${threshold}% staged rollout percentage`;
                                                                }
                                                            }

                                                            return (
                                                                <Space key={conditionIndex}>
                                                                    {condition.isConditionMet ?
                                                                        <CheckCircleFilled
                                                                            style={{ color: runwayColors.statusGreen }}
                                                                        />
                                                                    :   <ClockCircleFilled
                                                                            style={{ color: antGrey.grey6 }}
                                                                        />
                                                                    }
                                                                    <Text style={conditionTextStyle}>{text}</Text>
                                                                </Space>
                                                            );
                                                        },
                                                    )}
                                                    {automation.checklistItems?.map((cliCondition, cliIndex) => {
                                                        const condition = props.checklistItems.find(
                                                            (cli) => cli.id === cliCondition['id'],
                                                        );
                                                        const isCliCompleted =
                                                            condition?.status &&
                                                            (condition?.status?.status ===
                                                                ChecklistItemStatusType.Approved ||
                                                                condition?.status?.status ===
                                                                    ChecklistItemStatusType.Passed);
                                                        const selectedConditionOwners = getRolloutChecklistItemsOwners(
                                                            condition,
                                                            orgUsers,
                                                            orgGroups,
                                                        );
                                                        const text =
                                                            selectedConditionOwners.length === 0 ?
                                                                condition?.title
                                                            :   `${
                                                                    condition?.title
                                                                } (owned by ${selectedConditionOwners.join(', ')})`;
                                                        return (
                                                            <Space key={cliIndex}>
                                                                {isCliCompleted ?
                                                                    <CheckCircleFilled
                                                                        style={{ color: runwayColors.statusGreen }}
                                                                    />
                                                                :   <ClockCircleFilled
                                                                        style={{ color: antGrey.grey6 }}
                                                                    />
                                                                }
                                                                <Text style={conditionTextStyle}>{text}</Text>
                                                            </Space>
                                                        );
                                                    })}
                                                </ConditionsOrMetricsContainer>
                                            )}
                                            {shouldShowAutomationSteps && (
                                                <ConditionsOrMetricsContainer title="Steps">
                                                    {automation.steps.map((step, stepIndex) => {
                                                        return (
                                                            <Space key={stepIndex}>
                                                                {iconForAutomationStepStatus(step.status)}
                                                                <Text
                                                                    style={{
                                                                        ...conditionTextStyle,
                                                                    }}
                                                                >
                                                                    {step.description}
                                                                </Text>
                                                            </Space>
                                                        );
                                                    })}
                                                </ConditionsOrMetricsContainer>
                                            )}
                                            {shouldShowRollbackDiscardedText && (
                                                <Text type="secondary">
                                                    The prepared rollback was discarded by{' '}
                                                    {automation.discardedAutomaticallyByRunway === true ?
                                                        'Runway'
                                                    :   `${automation.discardedBy}`}{' '}
                                                    at{' '}
                                                    {dayjs(automation.discardedAt).format(DatetimeFormat.TimeShortDate)}
                                                    .
                                                </Text>
                                            )}
                                        </div>
                                    }
                                </Collapse.Panel>
                            </Collapse>
                        </PlanRestrictedFeatureWrapper>
                    );
                })}
            </div>
        </div>
    );
};

export default MonitoringAutomations;

const iconForMonitoringMetricStatus: (status?: MonitoringMetricHealthStatus) => React.ReactNode = (status) => {
    switch (status) {
        case MonitoringMetricHealthStatus.Healthy:
            return <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />;
        case MonitoringMetricHealthStatus.Unhealthy:
            return <CloseCircleFilled style={{ color: runwayColors.statusRed }} />;
        default:
            return <ClockCircleFilled style={{ color: antGrey.grey6 }} />;
    }
};

const ConditionsOrMetricsContainer: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
    const settingsHeaderStyle: React.CSSProperties = {
        textTransform: 'uppercase',
        color: antGrey.grey7,
        fontSize: 10,
        letterSpacing: 1.1,
    };

    return (
        <div
            style={{
                backgroundColor: 'white',
                borderRadius: 8,
                border: `1px solid ${antGrey.grey5}`,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                flex: 1,
            }}
        >
            <Text style={settingsHeaderStyle}>{title}</Text>
            {children}
        </div>
    );
};

const iconForAutomationStepStatus: (status: AutomationStatus) => React.ReactNode = (status) => {
    const color = colorForAutomationStatus(status, false);

    switch (status) {
        case AutomationStatus.Success:
            return <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />;
        case AutomationStatus.Pending:
            return <ClockCircleFilled style={{ color: antGrey.grey6 }} />;
        case AutomationStatus.Failed:
            return <CloseCircleFilled style={{ color: runwayColors.statusRed }} />;
        default:
            return iconForAutomationStatus(status, color, 16);
    }
};
